/**
 * Smart Choice Widget Event Utilities
 *
 * This module provides custom DOM events for cross-microfrontend communication.
 * Parent applications (like CEX) can listen to these events to sync Smart Choice
 * preferences with their own state management (e.g., Redux).
 *
 * @example
 * // In CEX (parent application):
 * import { SMART_CHOICE_EVENTS } from '@psx/parcels';
 *
 * useEffect(() => {
 *   const handlePreferencesUpdate = (e: CustomEvent) => {
 *     dispatch(setSmartChoicePreferences(e.detail.preferences));
 *   };
 *
 *   document.addEventListener(SMART_CHOICE_EVENTS.PREFERENCES_LOADED, handlePreferencesUpdate);
 *   document.addEventListener(SMART_CHOICE_EVENTS.PREFERENCES_SAVED, handlePreferencesUpdate);
 *
 *   return () => {
 *     document.removeEventListener(SMART_CHOICE_EVENTS.PREFERENCES_LOADED, handlePreferencesUpdate);
 *     document.removeEventListener(SMART_CHOICE_EVENTS.PREFERENCES_SAVED, handlePreferencesUpdate);
 *   };
 * }, [dispatch]);
 */

/**
 * Event names for Smart Choice widget communication
 */
export const SMART_CHOICE_EVENTS = {
  /** Fired when preferences are loaded from RecommendationPreferencesStatus API */
  PREFERENCES_LOADED: 'smart-choice:preferences-loaded',
  /** Fired when preferences are saved via SaveRecommendationPreferencesStatus API */
  PREFERENCES_SAVED: 'smart-choice:preferences-saved',
  /** Fired when there's an error loading or saving preferences */
  PREFERENCES_ERROR: 'smart-choice:preferences-error',
} as const;

/**
 * Type for Smart Choice event names
 */
export type SmartChoiceEventName = typeof SMART_CHOICE_EVENTS[keyof typeof SMART_CHOICE_EVENTS];

/**
 * Recommendation Preferences Status structure from API
 */
export interface RecommendationPreferencesData {
  uuid?: string;
  createdDate?: string;
  lastUpdatedDate?: string;
  distance?: string;
  evngApptAvbl?: string;
  wkndApptAvbl?: string;
  onlineApptSchdlAvbl?: string;
  virtualApptAvbl?: string;
  providerGender?: string;
  providerBoardCertStatus?: string;
  languagesSpoken?: string;
  hasDismissedCoachMark?: string;
  hasDismissedCoachMarkWeb?: string;
  hasDismissedPopover?: string;
  hasSetPreferences?: string;
  hasDefaultPreferences?: string;
  memberRandomizedKey?: string;
}

/**
 * Event detail structure for Smart Choice preference events
 */
export interface SmartChoicePreferencesEventDetail {
  /** The preferences data from the API */
  preferences: RecommendationPreferencesData;
  /** Source of the event - whether it came from a fetch or save operation */
  source: 'api-fetch' | 'api-save';
  /** ISO timestamp of when the event was dispatched */
  timestamp: string;
  /** The user ID associated with these preferences */
  userId?: string;
  /** Whether this was for a super user */
  isSuperUser?: boolean;
}

/**
 * Event detail structure for error events
 */
export interface SmartChoiceErrorEventDetail {
  /** Error message */
  message: string;
  /** The operation that failed */
  operation: 'fetch' | 'save';
  /** ISO timestamp of when the error occurred */
  timestamp: string;
  /** Original error object if available */
  error?: unknown;
}

/**
 * Dispatch a Smart Choice preferences event
 *
 * @param eventName - The event name from SMART_CHOICE_EVENTS
 * @param detail - The event detail payload
 *
 * @example
 * dispatchSmartChoiceEvent(SMART_CHOICE_EVENTS.PREFERENCES_LOADED, {
 *   preferences: apiResponse,
 *   source: 'api-fetch',
 *   timestamp: new Date().toISOString(),
 * });
 */
export const dispatchSmartChoiceEvent = (
  eventName: SmartChoiceEventName,
  detail: SmartChoicePreferencesEventDetail | SmartChoiceErrorEventDetail
): void => {
  const event = new CustomEvent(eventName, {
    detail,
    bubbles: true,
    composed: true, // Allows event to cross shadow DOM boundaries
  });
  document.dispatchEvent(event);
};

/**
 * Type-safe event listener for Smart Choice preference events
 *
 * @example
 * useSmartChoiceEventListener(SMART_CHOICE_EVENTS.PREFERENCES_LOADED, (detail) => {
 *   console.log('Preferences loaded:', detail.preferences);
 * });
 */
export type SmartChoiceEventListener = (
  detail: SmartChoicePreferencesEventDetail
) => void;

export type SmartChoiceErrorEventListener = (
  detail: SmartChoiceErrorEventDetail
) => void;

/**
 * Helper to create a typed event handler for Smart Choice events
 *
 * @param callback - The callback to handle the event detail
 * @returns An event handler function compatible with addEventListener
 */
export const createSmartChoiceEventHandler = (
  callback: SmartChoiceEventListener
): EventListener =>
  ((event: CustomEvent<SmartChoicePreferencesEventDetail>) => {
    callback(event.detail);
  }) as EventListener;

/**
 * Helper to create a typed error event handler for Smart Choice events
 *
 * @param callback - The callback to handle the error event detail
 * @returns An event handler function compatible with addEventListener
 */
export const createSmartChoiceErrorHandler = (
  callback: SmartChoiceErrorEventListener
): EventListener =>
  ((event: CustomEvent<SmartChoiceErrorEventDetail>) => {
    callback(event.detail);
  }) as EventListener;

